import React, { useCallback, useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import toast from 'react-hot-toast'
import AdminLayout from '../../components/Admin/AdminLayout'
import AdminModal from '../../components/Admin/AdminModal'
import { AdminStatCardFlex } from './adminUi'

const STATUS_OPTIONS = [
  { id: 'all', label: 'All statuses' },
  { id: 'delivered', label: 'Delivered' },
  { id: 'failed', label: 'Failed' },
  { id: 'skipped', label: 'Skipped' },
  { id: 'dev_fallback', label: 'Dev log only' },
]

const CHANNEL_OPTIONS = [
  { id: 'all', label: 'All channels' },
  { id: 'system_smtp', label: 'System SMTP' },
  { id: 'user_mailbox', label: 'User mailbox' },
]

const CATEGORY_LABELS = {
  general: 'General',
  registration: 'Registration code',
  password_reset: 'Password reset',
  login: 'Login code',
  mailbox_bind: 'Mailbox bind',
  company_transfer: 'Company email change',
  registration_rejection: 'Registration rejection',
  company_invite: 'Company invite',
  bulk_campaign: 'Email campaign',
  chat_login_reminder: 'Chat reminder',
  deal_alert: 'Deal alert',
  share_package: 'Share package',
  digest_summary: 'Digest summary',
  smtp_test: 'SMTP test',
  user_mailbox: 'User mailbox',
}

function formatDateTime(value) {
  if (!value) return '—'
  return new Date(value).toLocaleString('en-GB', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).replace(',', '')
}

function statusBadge(status) {
  const map = {
    delivered: 'success',
    failed: 'escalated',
    skipped: 'pending',
    dev_fallback: 'reviewing',
  }
  return map[status] || 'pending'
}

function categoryLabel(value) {
  return CATEGORY_LABELS[value] || value || '—'
}

function exportCsv(items) {
  const header = ['Time', 'To', 'From', 'Subject', 'Category', 'Status', 'SMTP Host', 'Provider Response', 'Error']
  const lines = items.map((row) => [
    row.created_at,
    row.to_email,
    row.from_email || '',
    row.subject,
    row.category,
    row.status,
    row.smtp_host || '',
    row.provider_response || '',
    row.error_message || '',
  ].map((c) => `"${String(c || '').replace(/"/g, '""')}"`).join(','))
  const blob = new Blob([[header.join(','), ...lines].join('\n')], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `email-send-logs-${new Date().toISOString().slice(0, 10)}.csv`
  a.click()
  URL.revokeObjectURL(url)
}

export default function AdminEmailLogs() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [statusFilter, setStatusFilter] = useState('all')
  const [categoryFilter, setCategoryFilter] = useState('all')
  const [channelFilter, setChannelFilter] = useState('all')
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [search, setSearch] = useState('')
  const [recipient, setRecipient] = useState('')
  const [detail, setDetail] = useState(null)
  const [detailLoading, setDetailLoading] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await axios.get('/api/admin/email-send-logs', {
        params: {
          page,
          pageSize: 20,
          status: statusFilter,
          category: categoryFilter,
          channel: channelFilter,
          fromDate: fromDate || undefined,
          toDate: toDate || undefined,
          search: search.trim() || undefined,
          recipient: recipient.trim() || undefined,
        },
      })
      setData(res.data)
    } catch {
      toast.error('Failed to load email send logs')
    } finally {
      setLoading(false)
    }
  }, [page, statusFilter, categoryFilter, channelFilter, fromDate, toDate, search, recipient])

  useEffect(() => {
    load()
  }, [load])

  const categoryOptions = useMemo(() => {
    const fromApi = data?.categories || []
    const merged = new Set([...fromApi, ...Object.keys(CATEGORY_LABELS)])
    return Array.from(merged).sort()
  }, [data?.categories])

  const openDetail = async (id) => {
    setDetailLoading(true)
    setDetail({ id, loading: true })
    try {
      const res = await axios.get(`/api/admin/email-send-logs/${id}`)
      setDetail(res.data.log)
    } catch {
      toast.error('Failed to load log detail')
      setDetail(null)
    } finally {
      setDetailLoading(false)
    }
  }

  const resetFilters = () => {
    setStatusFilter('all')
    setCategoryFilter('all')
    setChannelFilter('all')
    setFromDate('')
    setToDate('')
    setSearch('')
    setRecipient('')
    setPage(1)
  }

  const stats = data?.stats || {}
  const items = data?.items || []
  const totalPages = data?.totalPages || 1

  return (
    <AdminLayout title="Email Send Logs">
      <p style={{ margin: '0 0 16px', color: '#606266', fontSize: 13, lineHeight: 1.5 }}>
        Every system SMTP send is recorded here with timestamp, content, recipient, delivery status, and provider response
        (e.g. Tencent Exmail). Use this when contacting your mail provider about undelivered messages.
      </p>

      <div className="admin-stats-row cols-4" style={{ marginBottom: 20 }}>
        <AdminStatCardFlex icon="email" cls="blue" label="Matching records" value={loading && !data ? '…' : String(stats.total ?? 0)} />
        <AdminStatCardFlex icon="check" cls="green" label="Delivered" value={loading && !data ? '…' : String(stats.delivered ?? 0)} />
        <AdminStatCardFlex icon="alert" cls="red" label="Failed" value={loading && !data ? '…' : String(stats.failed ?? 0)} />
        <AdminStatCardFlex icon="clock" cls="orange" label="Skipped / Dev" value={loading && !data ? '…' : String(Number(stats.skipped || 0) + Number(stats.dev_fallback || 0))} />
      </div>

      <div className="admin-filter-bar" style={{ marginBottom: 16, flexWrap: 'wrap' }}>
        <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1) }}>
          {STATUS_OPTIONS.map((opt) => <option key={opt.id} value={opt.id}>{opt.label}</option>)}
        </select>
        <select value={categoryFilter} onChange={(e) => { setCategoryFilter(e.target.value); setPage(1) }}>
          <option value="all">All categories</option>
          {categoryOptions.map((cat) => (
            <option key={cat} value={cat}>{categoryLabel(cat)}</option>
          ))}
        </select>
        <select value={channelFilter} onChange={(e) => { setChannelFilter(e.target.value); setPage(1) }}>
          {CHANNEL_OPTIONS.map((opt) => <option key={opt.id} value={opt.id}>{opt.label}</option>)}
        </select>
        <input type="date" value={fromDate} onChange={(e) => { setFromDate(e.target.value); setPage(1) }} title="From date" />
        <input type="date" value={toDate} onChange={(e) => { setToDate(e.target.value); setPage(1) }} title="To date" />
        <input
          type="search"
          placeholder="Search subject / content"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { setPage(1); load() } }}
          style={{ minWidth: 180 }}
        />
        <input
          type="search"
          placeholder="Recipient email"
          value={recipient}
          onChange={(e) => setRecipient(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { setPage(1); load() } }}
          style={{ minWidth: 160 }}
        />
        <button type="button" className="admin-btn secondary" onClick={() => { setPage(1); load() }}>Search</button>
        <button type="button" className="admin-btn secondary" onClick={resetFilters}>Reset</button>
        <span className="admin-filter-spacer" />
        <button type="button" className="admin-btn secondary" disabled={!items.length} onClick={() => exportCsv(items)}>
          Export page CSV
        </button>
      </div>

      <div className="admin-table-card">
        <div className="admin-table-header">
          <span className="admin-table-title">Send history</span>
          <span style={{ fontSize: 12, color: '#909399' }}>
            {loading ? 'Loading…' : `${data?.total ?? 0} record(s)`}
          </span>
        </div>
        <table>
          <thead>
            <tr>
              <th style={{ width: 150 }}>Time</th>
              <th style={{ width: 190 }}>To</th>
              <th>Subject</th>
              <th style={{ width: 130 }}>Category</th>
              <th style={{ width: 100 }}>Status</th>
              <th style={{ width: 110 }}>Provider</th>
              <th className="admin-th-actions" style={{ width: 90 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {!loading && items.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ textAlign: 'center', color: '#909399', padding: 28 }}>
                  No email send logs yet. Logs appear after the next outgoing email.
                </td>
              </tr>
            ) : items.map((row) => (
              <tr key={row.id}>
                <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{formatDateTime(row.created_at)}</td>
                <td style={{ fontSize: 12, wordBreak: 'break-all' }}>{row.to_email}</td>
                <td>
                  <div style={{ fontWeight: 600, marginBottom: 2 }}>{row.subject || '—'}</div>
                  {row.body_preview ? (
                    <div style={{ fontSize: 12, color: '#909399', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {row.body_preview}
                    </div>
                  ) : null}
                </td>
                <td style={{ fontSize: 12 }}>{categoryLabel(row.category)}</td>
                <td>
                  <span className={`admin-status-badge ${statusBadge(row.status)}`}>{row.status}</span>
                </td>
                <td style={{ fontSize: 12 }}>{row.smtp_provider || row.smtp_host || '—'}</td>
                <td className="admin-td-actions">
                  <button type="button" className="admin-btn secondary" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => openDetail(row.id)}>
                    View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="admin-pagination" style={{ marginTop: 16 }}>
          <button type="button" className="admin-btn secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</button>
          <span style={{ fontSize: 13, color: '#606266' }}>Page {page} / {totalPages}</span>
          <button type="button" className="admin-btn secondary" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</button>
        </div>
      )}

      <AdminModal
        open={!!detail}
        title="Email send log"
        subtitle={detail?.subject || ''}
        wide
        onClose={() => setDetail(null)}
        footer={<button type="button" className="admin-btn primary" onClick={() => setDetail(null)}>Close</button>}
      >
        {detailLoading || detail?.loading ? (
          <p>Loading…</p>
        ) : detail ? (
          <div className="admin-email-log-detail">
            <div className="admin-settings-grid-2" style={{ marginBottom: 16 }}>
              <div><strong>Time</strong><div>{formatDateTime(detail.created_at)}</div></div>
              <div><strong>Status</strong><div><span className={`admin-status-badge ${statusBadge(detail.status)}`}>{detail.status}</span></div></div>
              <div><strong>To</strong><div style={{ wordBreak: 'break-all' }}>{detail.to_email}</div></div>
              <div><strong>From</strong><div style={{ wordBreak: 'break-all' }}>{detail.from_email || '—'}</div></div>
              <div><strong>Category</strong><div>{categoryLabel(detail.category)}</div></div>
              <div><strong>Channel</strong><div>{detail.channel}</div></div>
              <div><strong>SMTP host</strong><div>{detail.smtp_host || '—'}</div></div>
              <div><strong>SMTP provider</strong><div>{detail.smtp_provider || '—'}</div></div>
              <div><strong>Message ID</strong><div style={{ wordBreak: 'break-all' }}>{detail.message_id || '—'}</div></div>
              <div><strong>Duration</strong><div>{detail.duration_ms != null ? `${detail.duration_ms} ms` : '—'}</div></div>
            </div>

            {detail.skipped_reason ? (
              <div style={{ marginBottom: 12, padding: 12, background: '#f5f7fa', borderRadius: 8 }}>
                <strong>Skipped reason:</strong> {detail.skipped_reason}
              </div>
            ) : null}

            {detail.error_message ? (
              <div style={{ marginBottom: 12, padding: 12, background: '#fef2f2', borderRadius: 8, color: '#b91c1c' }}>
                <strong>Error:</strong> {detail.error_code ? `[${detail.error_code}] ` : ''}{detail.error_message}
              </div>
            ) : null}

            <div style={{ marginBottom: 12 }}>
              <strong>Provider response (Tencent / SMTP server)</strong>
              <pre className="admin-code-block">{detail.provider_response || '—'}</pre>
            </div>

            {(detail.accepted_recipients?.length || detail.rejected_recipients?.length) ? (
              <div className="admin-settings-grid-2" style={{ marginBottom: 12 }}>
                <div>
                  <strong>Accepted</strong>
                  <pre className="admin-code-block">{JSON.stringify(detail.accepted_recipients, null, 2)}</pre>
                </div>
                <div>
                  <strong>Rejected</strong>
                  <pre className="admin-code-block">{JSON.stringify(detail.rejected_recipients, null, 2)}</pre>
                </div>
              </div>
            ) : null}

            {detail.metadata ? (
              <div style={{ marginBottom: 12 }}>
                <strong>Metadata</strong>
                <pre className="admin-code-block">{JSON.stringify(detail.metadata, null, 2)}</pre>
              </div>
            ) : null}

            <div style={{ marginBottom: 12 }}>
              <strong>Plain text content</strong>
              <pre className="admin-code-block admin-code-block-scroll">{detail.body_text || '—'}</pre>
            </div>

            <div>
              <strong>HTML content</strong>
              <pre className="admin-code-block admin-code-block-scroll">{detail.body_html || '—'}</pre>
            </div>
          </div>
        ) : null}
      </AdminModal>
    </AdminLayout>
  )
}
